import { ChangeEvent, Component } from "react";
import "./FolderMetadataDialog.css";
import "react-image-crop/dist/ReactCrop.css";
import Action from "./../../../../formElements/Action/Action";
import Button from "components/ui/Button";
import { GhostButton } from "components/adminUi/Button";
import Input from "./../../../../formElements/Input/Input";
import Textarea from "./../../../../formElements/Textarea/Textarea";
import Dialog from "components/ui/Dialog";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { getErrorComponents } from "services/util";
import LibraryObjectViewType from "../../../LibraryObjectCard/LibraryObjectViewType";
import LibraryObjectCard from "./../../../LibraryObjectCard/LibraryObjectCard";
import { LibraryObject, EDITOR_FILE_NAME, FolderUI } from "services/content/model";
import {
  createFile,
  createFolder,
  updateFolderMetadata,
  renameFolder,
} from "../../../../../services/content";
import { Cropper, ImageCropperDialog } from "../ImageCropperDialog/ImageCropperDialog";
import { Translation } from "react-i18next";

export enum FolderMetadataDialogType {
  CREATE = "create",
  METADATA = "metadata",
  NAME = "name",
  FILE = "file",
}

export enum PreviewType {
  REGULAR = "preview",
  VERTICAL = "preview_vertical",
  HORIZONTAL = "preview_horizontal",
}
interface ContentDataType {
  xhtml: Document;
  json: object;
}

interface ComponentProps {
  showDialog: boolean;
  onClose: (...args: unknown[]) => void;
  currentKey: string;
  libraryObject?: LibraryObject;
  folderUIClass: FolderUI;
  type?: FolderMetadataDialogType;
  contentData?: ContentDataType;
  assetFiles?: File[];
  itemLabel?: string;
  refresh(): void;
}

interface ComponentState {
  object: LibraryObject;
  isFormSubmitted: boolean;
  errors: string;
  showSpinner: boolean;
  showPreviewVerticalSelectorDialog: boolean;
  showPreviewSelectorDialog: boolean;
  showPreviewHorizontalSelectorDialog: boolean;
  selectedFile: File | null;
  croppedFile: any;
  selectedFileData: string;
}

export default class FolderMetadataDialog extends Component<
  ComponentProps,
  ComponentState
> {
  state: ComponentState = {
    object: this.props.libraryObject,
    isFormSubmitted: false,
    errors: "",
    showSpinner: false,
    showPreviewVerticalSelectorDialog: false,
    showPreviewSelectorDialog: false,
    showPreviewHorizontalSelectorDialog: false,
    selectedFile: null,
    croppedFile: null,
  } as ComponentState;
  cropper: Cropper | null = null;
  type: FolderMetadataDialogType = this.props.type || FolderMetadataDialogType.CREATE;
  constructor(props: ComponentProps) {
    super(props);

    if (!this.props.libraryObject) {
      this.state.object = new LibraryObject();
      this.state.object.order = -1;
    }

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleFieldChanged: (
    fieldName: string
  ) => (evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void =
    (fieldName: string) => (evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const object: LibraryObject = this.state.object.clone();
      if (fieldName === "name") {
        object.name = evt.target.value;
      } else if (fieldName === "author") {
        object.author = evt.target.value;
      } else if (fieldName === "description") {
        object.description = evt.target.value;
      } else if (fieldName === "order") {
        object.order = parseInt(evt.target.value, 10);
      } else if (fieldName === "length") {
        const value = parseInt(evt.target.value, 10);
        object.length = value < 0 ? 0 : value;
      } else if (fieldName === "readings") {
        const value = parseInt(evt.target.value, 10);
        object.readings = value < 0 ? 0 : value;
      } else if (fieldName === "videos") {
        const value = parseInt(evt.target.value, 10);
        object.videos = value < 0 ? 0 : value;
      }
      this.setState({ object: object });
    };

  isFormValid(): boolean {
    return (
      this.state.object.name !== "" &&
      this.state.object.name.indexOf("/") === -1 &&
      (this.props.folderUIClass.uiFormFields.author.required
        ? this.state.object.author !== ""
        : true) &&
      (this.props.folderUIClass.uiFormFields.description.required
        ? this.state.object.description !== ""
        : true) &&
      (this.props.folderUIClass.uiFormFields.order.required
        ? this.state.object.order !== -1
        : true) &&
      (this.props.folderUIClass.uiFormFields.preview.required
        ? this.state.object.preview !== null
        : true) &&
      (this.props.folderUIClass.uiFormFields.preview_vertical.required
        ? this.state.object.preview_vertical !== null
        : true) &&
      (this.props.folderUIClass.uiFormFields.preview_horizontal.required
        ? this.state.object.preview_horizontal !== null
        : true) &&
      (this.props.folderUIClass.uiFormFields.readings.required
        ? this.state.object.readings !== null
        : true) &&
      (this.props.folderUIClass.uiFormFields.videos.required
        ? this.state.object.videos !== null
        : true)
    );
  }

  getSimpleFileName(fileName: string | undefined): string {
    if (!fileName) {
      return "";
    }

    if (fileName.length <= 8) {
      return fileName;
    }

    const lastIndexOfSlash = fileName.lastIndexOf("/");
    const lastIndexOfDot = fileName.lastIndexOf(".");

    if (lastIndexOfSlash !== -1) {
      if (lastIndexOfDot - lastIndexOfSlash < 5) {
        return fileName.slice(lastIndexOfSlash + 1);
      }
      return `${fileName.slice(lastIndexOfSlash + 1, lastIndexOfSlash + 6)}...${fileName.slice(lastIndexOfDot, lastIndexOfDot + 4)}`;
    } else {
      return `${fileName.substring(0, 5)}...${fileName.substring(lastIndexOfDot)}`;
    }
  }

  handleSubmit(): void {
    this.setState({ isFormSubmitted: true, errors: "" });
    if (!this.isFormValid()) {
      if (this.state.object.name.indexOf("/") !== -1) {
        this.setState({ errors: "Name cannot contain '/' characters." });
      }
      return;
    }
    this.setState({ showSpinner: true });
    let submitPromise = null;
    if (this.type === FolderMetadataDialogType.METADATA) {
      submitPromise = updateFolderMetadata(this.state.object);
    } else if (this.type === FolderMetadataDialogType.NAME) {
      submitPromise = renameFolder(this.state.object);
    } else if (this.type === FolderMetadataDialogType.FILE) {
      if (!this.props.contentData) {
        this.setState({ errors: "No content data passed to this component." });
        return;
      }
      submitPromise = createFile(
        this.state.object,
        this.props.currentKey,
        [
          new File(
            [new XMLSerializer().serializeToString(this.props.contentData!.xhtml)],
            `${this.state.object.name}.xhtml`,
            { type: "application/xhtml+xml" }
          ),
          new File([JSON.stringify(this.props.contentData!.json)], EDITOR_FILE_NAME, {
            type: "application/json",
          }),
        ],
        this.props.assetFiles
      );
    } else {
      submitPromise = createFolder(this.state.object, this.props.currentKey);
    }
    submitPromise
      .then(() => {
        this.setState({ showSpinner: false });
        this.props.onClose();
        this.props.refresh();
      })
      .catch(errors => {
        this.setState({ showSpinner: false, errors: errors });
      });
  }

  onCloseDialog = () => {
    this.setState({
      showPreviewVerticalSelectorDialog: false,
      showPreviewSelectorDialog: false,
      showPreviewHorizontalSelectorDialog: false,
      selectedFile: null,
      selectedFileData: "",
    });
  };
  openSelectorDialog(preview: string) {
    if (preview === PreviewType.REGULAR) {
      this.setState({
        showPreviewSelectorDialog: true,
      });
    } else if (preview === PreviewType.VERTICAL) {
      this.setState({
        showPreviewVerticalSelectorDialog: true,
      });
    } else if (preview === PreviewType.HORIZONTAL) {
      this.setState({
        showPreviewHorizontalSelectorDialog: true,
      });
    }
  }
  uploadPreview = (args: any) => {
    const object: LibraryObject = this.state.object.clone();
    object.preview = args.file;
    object.preview_url = args.preview;
    this.setState({
      object: object,
      showPreviewSelectorDialog: false,
      selectedFile: null,
      selectedFileData: "",
    });
  };
  uploadPreviewVertical = (args: any) => {
    const object: LibraryObject = this.state.object.clone();
    object.preview_vertical = args.file;
    object.preview_vertical_url = args.preview;
    this.setState({
      object: object,
      showPreviewVerticalSelectorDialog: false,
      selectedFile: null,
      selectedFileData: "",
    });
  };
  uploadPreviewHorizontal = (args: any) => {
    const object: LibraryObject = this.state.object.clone();
    object.preview_horizontal = args.file;
    object.preview_horizontal_url = args.preview;
    this.setState({
      object: object,
      showPreviewHorizontalSelectorDialog: false,
      selectedFile: null,
      selectedFileData: "",
    });
  };
  onFileChangePreview = (
    event: ChangeEvent<HTMLInputElement>,
    previewType: PreviewType
  ) => {
    if (event.target !== null && event.target.files !== null) {
      if (event.target.files.length > 0) {
        const file = event.target.files[0];
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
          if (e !== null && e.target !== null) {
            const object: LibraryObject = this.state.object.clone();

            object[`${previewType}_url`] = e.target.result;
            this.setState({ object: object, selectedFileData: e.target.result });
          }
        };
        reader.addEventListener("load", () =>
          this.setState({
            selectedFile: file,
          })
        );
        const object: LibraryObject = this.state.object.clone();
        object[previewType] = file;
        this.setState({
          object: object,
        });
        reader.readAsDataURL(file);
      }
    }
  };

  renderFieldLabel = (modelObj: { [key: string]: any }) => {
    if (!modelObj.required) {
      return <Translation>{t => t("Common." + modelObj.label)}</Translation>;
    }

    return <Translation>{t => <>* {t("Common." + modelObj.label)}</>}</Translation>;
  };

  // The kit ✕/Escape call this with no argument — never forward their event.
  handleClose = () => {
    this.props.onClose();
  };

  render() {
    let folderUIClass = JSON.parse(JSON.stringify(this.props.folderUIClass));
    let dialogTitle = "";
    if (this.type === FolderMetadataDialogType.FILE) {
      dialogTitle = "Final details";
    } else if (this.type === FolderMetadataDialogType.CREATE) {
      dialogTitle =
        (this.type === FolderMetadataDialogType.CREATE ? "Add" : "Edit") +
        " " +
        folderUIClass.label;
    }

    return (
      <Translation>
        {t => (
          <Dialog
            className="admin-form-dialog folder-metadata"
            size="lg"
            open={this.props.showDialog}
            onClose={this.handleClose}
            closeOnOverlayClick={false}
            title={dialogTitle ? t("Common." + dialogTitle) : ""}
            footer={
              <>
                <GhostButton
                  className="cancel"
                  onClick={this.handleClose}
                  disabled={this.state.showSpinner}
                >
                  {t("Common.Cancel")}
                </GhostButton>
                <Button
                  variant="primary"
                  className="submit"
                  onClick={this.handleSubmit}
                  disabled={this.state.showSpinner}
                >
                  {t("Common.Save")}
                </Button>
              </>
            }
          >
            {this.state.showSpinner && (
              <div className="dialog-inline-spinner">
                <TalaeraSpinner />
              </div>
            )}
            {this.state.errors ? getErrorComponents(this.state.errors) : null}
            <div className="form-fields">
              <div>
                <label htmlFor="folder-name">
                  {this.renderFieldLabel(folderUIClass.uiFormFields.name)}{" "}
                  {this.props.itemLabel ? " of " + this.props.itemLabel : ""}
                </label>
              </div>
              <div className="width-100">
                <Input
                  name="folder-name"
                  id="folder-name"
                  type="text"
                  value={this.state.object.name}
                  onChange={this.handleFieldChanged("name")}
                  required={folderUIClass.uiFormFields.name.required}
                  formSubmitted={this.state.isFormSubmitted}
                  maxLength={folderUIClass.uiFormFields.name.maxLength}
                  disabled={this.type === FolderMetadataDialogType.METADATA}
                />
              </div>
              {folderUIClass.uiFormFields.author.isVisible && (
                <>
                  <div>
                    <label htmlFor="folder-author">
                      {this.renderFieldLabel(folderUIClass.uiFormFields.author)}
                    </label>
                  </div>
                  <div>
                    <Input
                      name="folder-author"
                      id="folder-author"
                      type="text"
                      value={this.state.object.author || ""}
                      onChange={this.handleFieldChanged("author")}
                      required={folderUIClass.uiFormFields.author.required}
                      formSubmitted={this.state.isFormSubmitted}
                      maxLength={folderUIClass.uiFormFields.author.maxLength}
                      disabled={this.type === FolderMetadataDialogType.NAME}
                    />
                  </div>
                </>
              )}
              {folderUIClass.uiFormFields.description.isVisible && (
                <>
                  <div>
                    <label htmlFor="folder-description">
                      {this.renderFieldLabel(folderUIClass.uiFormFields.description)}
                    </label>
                  </div>
                  <div>
                    <Textarea
                      name="folder-description"
                      id="folder-description"
                      value={this.state.object.description || ""}
                      required={folderUIClass.uiFormFields.description.required}
                      formSubmitted={this.state.isFormSubmitted}
                      onChange={this.handleFieldChanged("description")}
                      maxLength={folderUIClass.uiFormFields.description.maxLength}
                      disabled={this.type === FolderMetadataDialogType.NAME}
                    />
                  </div>
                </>
              )}
              {folderUIClass.uiFormFields.order.isVisible && (
                <>
                  <div>
                    <label htmlFor="folder-order">
                      {this.renderFieldLabel(folderUIClass.uiFormFields.order)}
                    </label>
                  </div>
                  <div>
                    <Input
                      name="folder-order"
                      id="folder-order"
                      type="number"
                      value={this.state.object.order}
                      onChange={this.handleFieldChanged("order")}
                      required={folderUIClass.uiFormFields.order.required}
                      formSubmitted={this.state.isFormSubmitted}
                      disabled={this.type === FolderMetadataDialogType.NAME}
                    />
                  </div>
                </>
              )}
              {folderUIClass.uiFormFields.length.isVisible && (
                <>
                  <div>
                    <label htmlFor="folder-order">
                      {this.renderFieldLabel(folderUIClass.uiFormFields.length)}
                    </label>
                  </div>
                  <div>
                    <Input
                      name="folder-length"
                      id="folder-length"
                      type="number"
                      min={0}
                      value={this.state.object.length}
                      onChange={this.handleFieldChanged("length")}
                      required={folderUIClass.uiFormFields.length.required}
                      formSubmitted={this.state.isFormSubmitted}
                      disabled={this.type === FolderMetadataDialogType.NAME}
                    />
                  </div>
                </>
              )}
              {folderUIClass.uiFormFields.readings.isVisible && (
                <>
                  <div>
                    <label htmlFor="folder-readings">
                      {this.renderFieldLabel(folderUIClass.uiFormFields.readings)}
                    </label>
                  </div>
                  <div>
                    <Input
                      name="folder-readings"
                      id="folder-readings"
                      type="number"
                      min={0}
                      value={this.state.object.readings}
                      onChange={this.handleFieldChanged("readings")}
                      required={folderUIClass.uiFormFields.readings.required}
                      formSubmitted={this.state.isFormSubmitted}
                      disabled={this.type === FolderMetadataDialogType.NAME}
                    />
                  </div>
                </>
              )}
              {folderUIClass.uiFormFields.videos.isVisible && (
                <>
                  <div>
                    <label htmlFor="folder-videos">
                      {this.renderFieldLabel(folderUIClass.uiFormFields.videos)}
                    </label>
                  </div>
                  <div>
                    <Input
                      name="folder-videos"
                      id="folder-videos"
                      type="number"
                      min={0}
                      value={this.state.object.videos}
                      onChange={this.handleFieldChanged("videos")}
                      required={folderUIClass.uiFormFields.videos.required}
                      formSubmitted={this.state.isFormSubmitted}
                      disabled={this.type === FolderMetadataDialogType.NAME}
                    />
                  </div>
                </>
              )}
              <div style={{ display: "flex" }}>
                {folderUIClass.uiFormFields.preview.isVisible &&
                  this.type !== FolderMetadataDialogType.NAME && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginRight: 10,
                      }}
                    >
                      <div>
                        <label htmlFor="folder-preview">
                          {this.renderFieldLabel(folderUIClass.uiFormFields.preview)}
                        </label>
                        {this.state.isFormSubmitted &&
                          folderUIClass.uiFormFields.preview.required &&
                          !this.state.object.preview && (
                            <div className="input-container">
                              <div className="input-tooltip required width-100">
                                * Required field
                              </div>
                            </div>
                          )}
                      </div>
                      <div>
                        <Action
                          className="btn"
                          style={{ marginTop: "10px", color: "#01bcd4" }}
                          onClick={() => this.openSelectorDialog(PreviewType.REGULAR)}
                        >
                          Upload{" "}
                          {this.getSimpleFileName(
                            this.state.object.preview?.name ||
                              this.state.object.preview_url
                          ) || " No File Chosen"}
                        </Action>
                        {this.state.showPreviewSelectorDialog && (
                          <ImageCropperDialog
                            file={this.state.selectedFile}
                            selectedFileData={this.state.selectedFileData}
                            showDialog={this.state.showPreviewSelectorDialog}
                            onClose={this.onCloseDialog}
                            aspect={1}
                            onFileChange={event =>
                              this.onFileChangePreview(event, PreviewType.REGULAR)
                            }
                            onSubmitPreview={this.uploadPreview}
                          />
                        )}
                      </div>
                    </div>
                  )}
                {folderUIClass.uiFormFields.preview_vertical.isVisible &&
                  this.type !== FolderMetadataDialogType.NAME && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginRight: 10,
                      }}
                    >
                      <div>
                        <label htmlFor="folder-preview">
                          {this.renderFieldLabel(
                            folderUIClass.uiFormFields.preview_vertical
                          )}
                        </label>
                        {this.state.isFormSubmitted &&
                          folderUIClass.uiFormFields.preview_vertical.required &&
                          !this.state.object.preview_vertical && (
                            <div className="input-container">
                              <div className="input-tooltip required width-100">
                                * Required field
                              </div>
                            </div>
                          )}
                      </div>
                      <div>
                        <Action
                          className="btn"
                          style={{ marginTop: "10px", color: "#01bcd4" }}
                          onClick={() => this.openSelectorDialog(PreviewType.VERTICAL)}
                        >
                          Upload{" "}
                          {this.getSimpleFileName(
                            this.state.object.preview_vertical?.name ||
                              this.state.object.preview_vertical_url
                          ) || " No File Chosen"}
                        </Action>
                        {this.state.showPreviewVerticalSelectorDialog && (
                          <ImageCropperDialog
                            file={this.state.selectedFile}
                            selectedFileData={this.state.selectedFileData}
                            showDialog={this.state.showPreviewVerticalSelectorDialog}
                            onClose={this.onCloseDialog}
                            aspect={9 / 16}
                            onFileChange={event =>
                              this.onFileChangePreview(event, PreviewType.VERTICAL)
                            }
                            onSubmitPreview={this.uploadPreviewVertical}
                          />
                        )}
                      </div>
                    </div>
                  )}
                {folderUIClass.uiFormFields.preview_horizontal.isVisible &&
                  this.type !== FolderMetadataDialogType.NAME && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginRight: 10,
                      }}
                    >
                      <div>
                        <label htmlFor="folder-preview">
                          {this.renderFieldLabel(
                            folderUIClass.uiFormFields.preview_horizontal
                          )}
                        </label>
                        {this.state.isFormSubmitted &&
                          folderUIClass.uiFormFields.preview_horizontal.required &&
                          !this.state.object.preview_horizontal && (
                            <div className="input-container">
                              <div className="input-tooltip required width-100">
                                * Required field
                              </div>
                            </div>
                          )}
                      </div>
                      <div>
                        <Action
                          className="btn"
                          style={{ marginTop: "10px", color: "#01bcd4" }}
                          onClick={() => this.openSelectorDialog(PreviewType.HORIZONTAL)}
                        >
                          Upload{" "}
                          {this.getSimpleFileName(
                            this.state.object.preview_horizontal?.name ||
                              this.state.object.preview_horizontal_url
                          ) || " No File Chosen"}
                        </Action>
                        {this.state.showPreviewHorizontalSelectorDialog && (
                          <ImageCropperDialog
                            file={this.state.selectedFile}
                            selectedFileData={this.state.selectedFileData}
                            showDialog={this.state.showPreviewHorizontalSelectorDialog}
                            aspect={16 / 9}
                            onClose={this.onCloseDialog}
                            onFileChange={event =>
                              this.onFileChangePreview(event, PreviewType.HORIZONTAL)
                            }
                            onSubmitPreview={this.uploadPreviewHorizontal}
                          />
                        )}
                      </div>
                    </div>
                  )}
              </div>
              {folderUIClass.uiFormFields.preview.isVisible &&
                this.state.object.preview_url && (
                  <div>
                    <h4>{t("Common.Preview")}</h4>
                    <LibraryObjectCard
                      object={this.state.object}
                      type={LibraryObjectViewType.LIST}
                      hasLesson={true}
                      ribbon={"Square"}
                    />
                  </div>
                )}
              {folderUIClass.uiFormFields.preview_vertical.isVisible &&
                this.state.object.preview_vertical_url && (
                  <div>
                    <h4>{t("Common.Preview")}</h4>
                    <LibraryObjectCard
                      object={this.state.object}
                      type={LibraryObjectViewType.LIST}
                      hasLesson={false}
                      ribbon={"Vertical"}
                    />
                  </div>
                )}
              {folderUIClass.uiFormFields.preview_vertical.isVisible &&
                this.state.object.preview_horizontal_url && (
                  <div>
                    <h4>{t("Common.Preview")}</h4>
                    <LibraryObjectCard
                      object={this.state.object}
                      type={LibraryObjectViewType.LIST}
                      listType={LibraryObjectViewType.FOLDER}
                      ribbon={"Horizontal"}
                    />
                  </div>
                )}
            </div>
          </Dialog>
        )}
      </Translation>
    );
  }
}
